import { z } from 'zod';
export const zExerciseChangeType = z.enum([
    'exercise_added',
    'exercise_removed',
    'exercise_updated',
    'exercise_prescription_change',
]);
export const zDateChangeType = z.enum(['assigned_date_changed']);
export const zTabletChangeType = z.enum([
    'tablet_exercise_added',
    'tablet_prescription_change',
    'tablet_exercise_updated',
    'tablet_exercise_removed',
]);
export const zWorkoutChangeType = z.union([
    zExerciseChangeType,
    zDateChangeType,
    zTabletChangeType,
]);
export const zFilterLogs = z.object({
    athletes: z.array(z.uuid()).optional(),
    changeTypes: z.array(zWorkoutChangeType).optional(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
    offset: z.number().optional(),
    limit: z.number().positive().optional(),
});
export function findExerciseNameById(workout, workoutExerciseId) {
    if (!workout || !workoutExerciseId) {
        return undefined;
    }
    for (const section of workout.sections) {
        for (const entry of section.entries) {
            if (entry.__type === 'WorkoutExercise' &&
                entry.workoutExerciseId === workoutExerciseId) {
                return entry.exerciseName;
            }
            else if (entry.__type === 'WorkoutSuperset') {
                for (const exercise of entry.exercises) {
                    if (exercise.exercise.workoutExerciseId === workoutExerciseId) {
                        return exercise.exercise.exerciseName;
                    }
                }
            }
        }
    }
    return undefined; // Return undefined if not found
}
export function getLibraryExerciseIdsByWorkout(workout) {
    const exerciseIds = {};
    function processEntry(entry) {
        if (entry.__type === 'WorkoutExercise') {
            exerciseIds[entry.workoutExerciseId] = entry.libraryExerciseId;
        }
        else if (entry.__type === 'WorkoutSuperset') {
            entry.exercises.forEach((supersetExercise) => {
                exerciseIds[supersetExercise.exercise.workoutExerciseId] =
                    supersetExercise.exercise.libraryExerciseId;
            });
        }
        else if (entry.__type === 'WorkoutAlternateGroup') {
            entry.exercises.forEach((alternateExercise) => {
                exerciseIds[alternateExercise.exercise.workoutExerciseId] =
                    alternateExercise.exercise.libraryExerciseId;
            });
        }
    }
    workout.sections.forEach((section) => {
        section.entries.forEach(processEntry);
    });
    return exerciseIds;
}
export const findExerciseByWorkoutExerciseId = (workout, exerciseId) => {
    for (const section of workout.sections) {
        for (const entry of section.entries) {
            if (entry.__type === 'WorkoutExercise' &&
                entry.workoutExerciseId === exerciseId) {
                return entry;
            }
            else if (entry.__type === 'WorkoutSuperset') {
                const supersetExercise = entry.exercises.find((e) => e.exercise.workoutExerciseId === exerciseId);
                if (supersetExercise) {
                    return supersetExercise.exercise;
                }
            }
        }
    }
    return undefined;
};
export function checkDateChange(original, changedWorkout) {
    return original.assignedDate !== changedWorkout.assignedDate
        ? [{ type: 'assigned_date_changed' }]
        : [];
}
export function checkExerciseChanges(originalExercises, changedExercises) {
    const deltas = [];
    for (const [id, libId] of Object.entries(changedExercises)) {
        if (!(id in originalExercises)) {
            deltas.push({
                type: 'exercise_added',
                workoutExerciseId: id,
            });
        }
        else if (originalExercises[id] !== libId) {
            deltas.push({
                type: 'exercise_updated',
                workoutExerciseId: id,
            });
        }
    }
    for (const id of Object.keys(originalExercises)) {
        if (!(id in changedExercises)) {
            deltas.push({
                type: 'exercise_removed',
                workoutExerciseId: id,
            });
        }
    }
    return deltas;
}
export function checkSetsAndMetricsChanges(original, changedWorkout, exerciseIds) {
    return exerciseIds
        .filter((id) => {
        const originalExercise = findExerciseByWorkoutExerciseId(original, id);
        const changedExercise = findExerciseByWorkoutExerciseId(changedWorkout, id);
        return (originalExercise &&
            changedExercise &&
            (JSON.stringify(originalExercise.metrics) !==
                JSON.stringify(changedExercise.metrics) ||
                JSON.stringify(originalExercise.sets) !==
                    JSON.stringify(changedExercise.sets)));
    })
        .map((id) => ({
        type: 'exercise_prescription_change',
        workoutExerciseId: id,
    }));
}
export function getUnchangedExerciseIds(originalExercises, changedExercises, exerciseChanges) {
    const changedExerciseIds = new Set(exerciseChanges.map((delta) => delta.workoutExerciseId));
    return Object.keys(changedExercises).filter((id) => !changedExerciseIds.has(id));
}
export function workoutDelta(original, changedWorkout) {
    const originalExercises = getLibraryExerciseIdsByWorkout(original);
    const changedExercises = getLibraryExerciseIdsByWorkout(changedWorkout);
    const exerciseChanges = checkExerciseChanges(originalExercises, changedExercises);
    const unchangedExerciseIds = getUnchangedExerciseIds(originalExercises, changedExercises, exerciseChanges);
    const setsAndMetricsChanges = checkSetsAndMetricsChanges(original, changedWorkout, unchangedExerciseIds);
    return [
        ...checkDateChange(original, changedWorkout),
        ...exerciseChanges,
        ...setsAndMetricsChanges,
    ];
}
//# sourceMappingURL=workout-delta.js.map